package ui.login


import androidx.compose.foundation.Image
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.autofill.ContentType
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.input.key.*
import androidx.compose.ui.semantics.contentType
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import compose.assets.Svg
import compose.assets.svg.Account
import compose.assets.svg.Eye_closed
import compose.assets.svg.Eye_outline
import compose.assets.svg.Key
import io.Repository
import provider.LocalDisplayProvider
import ssoft.frontend.generated.resources.*
import org.ssoft.common.server.portal.PortalIO
import org.jetbrains.compose.resources.painterResource
import androidx.compose.ui.layout.ContentScale
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState
import tools.rememberString
import ui.ArgsInitial
import ui.widget.*

@Composable
@ComposableNavNode(ArgsInitial::class)
fun LoginUI() {
    with(LoginScope.rememberScope()) {
        when (vm.state) {
            is DataState.Idle,
            is DataState.Result -> Content()

            is DataState.Error -> ErrUI()
            is DataState.Loading -> LoadingUI()

        }
    }
}

@Composable
fun LoginScope.Content() {
    val passChangeState = DialogState.remember()
    PassChangeDialog(passChangeState)

    val display = LocalDisplayProvider.current

    Box(Modifier.fillMaxSize()) {
        if (!display.isMobile) {
            Box(Modifier.fillMaxSize()) {
                Row(Modifier.height(500.dp).align(Alignment.Center)) {
                    Spacer(Modifier.weight(0.2F))
                    Box(
                        modifier = Modifier.weight(1F).fillMaxHeight()
                    ) {
                        BrandingLogo(
                            logoId = Repository.serverInfo?.imageLogoSquare,
                            modifier = Modifier.width(300.dp)
                                .align(Alignment.CenterEnd)
                        )
                    }
                    Space(128.dp)
                    Box(Modifier.weight(1F).fillMaxHeight()) {
                        Column(Modifier.align(Alignment.CenterStart)) {
                            Title(
                                modifier = Modifier.align(Alignment.CenterHorizontally),
                                style = MaterialTheme.typography.displayMedium
                            )
                            Space(64.dp)
                            InputForm(
                                passChangeState = passChangeState,
                                modifier = Modifier
                            )
                        }
                    }
                    Spacer(Modifier.weight(0.2F))
                }
            }
        } else {
            if (display.isLand()) {
                Row {
                    Box(Modifier.weight(1F).fillMaxHeight()) {
                        Column(
                            modifier = Modifier.align(Alignment.Center).padding(top = 32.dp),
                            verticalArrangement = Arrangement.spacedBy(32.dp)
                        ) {
                            Title(Modifier.Companion.align(Alignment.CenterHorizontally))
                            BrandingLogo(
                                modifier = Modifier.fillMaxSize().padding(bottom = 32.dp),
                                logoId = Repository.serverInfo?.imageLogoSquare
                            )
                        }
                    }
                    Box(Modifier.weight(1F).fillMaxHeight()) {
                        InputForm(passChangeState, Modifier.align(Alignment.Center))
                    }
                }
            } else {
                Column(
                    modifier = Modifier.align(Alignment.Center).padding(top = 32.dp),
                    verticalArrangement = Arrangement.spacedBy(32.dp)
                ) {
                    Title(Modifier.Companion.align(Alignment.CenterHorizontally))
                    InputForm(passChangeState, Modifier.align(Alignment.CenterHorizontally))
                    BrandingLogo(
                        modifier = Modifier.fillMaxSize().padding(horizontal = 32.dp),
                        logoId = Repository.serverInfo?.imageLogoSquare
                    )
                }
            }
        }
    }
}

@Suppress("UnusedReceiverParameter")
@Composable
private fun LoginScope.Title(
    modifier: Modifier,
    style: TextStyle = MaterialTheme.typography.displaySmall
) {
    Text(
        text = Res.string.login__title.rememberString(),
        fontWeight = FontWeight.Bold,
        style = style,
        modifier = modifier
    )
}

@Composable
private fun LoginScope.InputForm(passChangeState: DialogState, modifier: Modifier) {
    val focusPass = remember { FocusRequester() }

    fun callLogin() {
        action(LoginVM.Action.Login {
            passChangeState.show()
        })
    }
    Column(modifier) {
        Card(modifier = Modifier.width(300.dp)) {
            Column(
                Modifier.padding(horizontal = 24.dp)
                    .padding(bottom = 24.dp, top = 12.dp)
            ) {
                TextField(
                    value = vm.name,
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth()
                        .semantics { contentType = ContentType.Username }
                        .onKeyEvent { event ->
                            event.key.takeIf { event.type == KeyEventType.KeyUp && it in arrayOf(Key.Tab, Key.Enter) }
                                ?.run { focusPass.requestFocus() }
                                ?.let { true }
                                ?: false
                        },
                    leadingIcon = { Icon(Svg.Account, null) },
                    placeholder = { Text(Res.string.login__name.rememberString()) },
                    keyboardOptions = KeyboardOptions(
                        imeAction = ImeAction.Next
                    ),
                    keyboardActions = KeyboardActions(
                        onNext = { focusPass.requestFocus() },
                    ),
                    isError = vm.error,
                    onValueChange = {
                        if (!it.text.contains("\t") && !it.text.contains("\n")) {
                            vm.name = it
                        }
                        vm.error = false
                    })
                Space(8.dp)
                TextField(
                    value = vm.pass,
                    leadingIcon = { Icon(Svg.Key, null) },
                    singleLine = true,
                    trailingIcon = {
                        Icon(
                            imageVector = if (vm.showPass) Svg.Eye_outline else Svg.Eye_closed,
                            contentDescription = null,
                            modifier = Modifier
                                .clip(CircleShape)
                                .clickable { vm.showPass = !vm.showPass }
                                .padding(2.dp)
                        )
                    },
                    placeholder = { Text(Res.string.login__pass.rememberString()) },
                    isError = vm.error,
                    modifier = Modifier.fillMaxWidth()
                        .semantics { contentType = ContentType.Password }
                        .focusRequester(focusPass)
                        .onKeyEvent { event ->
                            event.key.takeIf { event.type == KeyEventType.KeyDown && it in arrayOf(Key.Tab, Key.Enter) }
                                ?.run { callLogin() }
                                ?.let { true }
                                ?: false
                        },
                    keyboardOptions = KeyboardOptions(
                        imeAction = ImeAction.Done
                    ),
                    keyboardActions = KeyboardActions(
                        onDone = { callLogin() },
                    ),
                    visualTransformation =
                        if (vm.showPass) VisualTransformation.None
                        else PasswordVisualTransformation(),
                    onValueChange = {
                        if (!it.text.contains("\t") && !it.text.contains("\n")) {
                            vm.pass = it
                        }
                        vm.error = false
                    })
            }
        }
        Space(16.dp)
        Button(
            onClick = { callLogin() },
            modifier = Modifier.width(300.dp)
                .align(Alignment.CenterHorizontally)
        ) {
            Text(Res.string.login__confirm.rememberString())
        }
    }
}

@Suppress("UnusedReceiverParameter")
@Composable
private fun LoginScope.BrandingLogo(modifier: Modifier, logoId: Long?) {
    Box(modifier) {
        logoId?.also {
            if (it == PortalIO.ImplServer.DEFAULT_IMAGE_SQUARE) {
                Image(
                    painter = painterResource(Res.drawable.logo_login_dark),
                    contentDescription = null,
                    contentScale = ContentScale.Fit,
                    modifier = Modifier.fillMaxSize()
                )
            } else {
                LazyImage(
                    id = it,
                    modifier = Modifier.fillMaxSize(),
                    alphaBackground = false
                )
            }
        }
    }
}

@Composable
fun LoginScope.PassChangeDialog(passChangeState: DialogState) {
    Dialog(passChangeState) {
        var pass0 by remember { mutableStateOf(TextFieldValue()) }
        var pass1 by remember { mutableStateOf(TextFieldValue()) }
        var passErr0 by remember { mutableStateOf(false) }
        var passErr1 by remember { mutableStateOf(false) }
        var passShow0 by remember { mutableStateOf(false) }
        var passShow1 by remember { mutableStateOf(false) }

        val focusPass0 = remember { FocusRequester() }
        val focusPass1 = remember { FocusRequester() }

        fun isPass0Valid(): Boolean {
            return pass0.text
                .trim()
                .takeIf { it.length >= 8 }
                ?.takeIf { it.any { it.isLetter() && it.isLowerCase() } }
                ?.takeIf { it.any { it.isLetter() && it.isUpperCase() } }
                ?.takeIf { it.any { !it.isLetter() } }
                .let { it != null }
        }

        fun isPass1Valid(): Boolean {
            val p0 = pass0.text.trim()
            val p1 = pass1.text.trim()
            return p0 == p1
        }

        fun callLogin() {
            if (isPass0Valid() && isPass1Valid()) {
                action(LoginVM.Action.ChangePassword(pass0.text.trim()))
                passChangeState.hide()
            } else {
                focusPass0.requestFocus()
            }
        }
        DialogUI(
            modifier = Modifier.align(Alignment.Center),
            onDismissRequest = { passChangeState.hide() },
            title = { Text(Res.string.login__pass_change_required.rememberString()) },
            confirmButton = {
                TextButton(
                    enabled = !passErr0 && !passErr1,
                    onClick = {
                        callLogin()
                    }) {
                    Text(Res.string.dialog__action_ok.rememberString())
                }
            }) {
            Column {
                TextField(
                    value = pass0,
                    leadingIcon = { Icon(Svg.Key, null) },
                    trailingIcon = {
                        Icon(
                            imageVector = if (passShow0) Svg.Eye_outline else Svg.Eye_closed,
                            contentDescription = null,
                            modifier = Modifier
                                .clip(CircleShape)
                                .clickable { passShow0 = !passShow0 }
                                .padding(2.dp)
                        )
                    },
                    placeholder = { Text(Res.string.login__pass_hint.rememberString()) },
                    isError = passErr0,
                    modifier = Modifier.fillMaxWidth()
                        .focusRequester(focusPass0)
                        .onKeyEvent {
                            it.key.takeIf { it == Key.Tab }
                                ?.run { focusPass1.requestFocus() }
                                ?.let { true }
                                ?: false
                        },
                    visualTransformation =
                        if (passShow0) VisualTransformation.None
                        else PasswordVisualTransformation(),
                    onValueChange = {
                        pass0 = it
                        passErr0 = !isPass0Valid()
                        passErr1 = !isPass1Valid()
                    })
                Space(16.dp)
                TextField(
                    value = pass1,
                    leadingIcon = { Icon(Svg.Key, null) },
                    trailingIcon = {
                        Icon(
                            imageVector = if (passShow1) Svg.Eye_outline else Svg.Eye_closed,
                            contentDescription = null,
                            modifier = Modifier
                                .clip(CircleShape)
                                .clickable { passShow1 = !passShow1 }
                                .padding(2.dp)
                        )
                    },
                    placeholder = { Text(Res.string.login__pass_hint_again.rememberString()) },
                    isError = passErr1,
                    modifier = Modifier.fillMaxWidth()
                        .focusRequester(focusPass1)
                        .onKeyEvent {
                            it.key.takeIf { it == Key.Tab }
                                ?.run { callLogin() }
                                ?.let { true }
                                ?: false
                        },
                    visualTransformation =
                        if (passShow1) VisualTransformation.None
                        else PasswordVisualTransformation(),
                    onValueChange = {
                        pass1 = it
                        passErr0 = !isPass0Valid()
                        passErr1 = !isPass1Valid()
                    })
                Space(16.dp)
                Text(Res.string.login__pass_change_condition_title.rememberString(), style = MaterialTheme.typography.titleSmall)
                Space(4.dp)
                Text(Res.string.login__pass_change_condition.rememberString(), style = MaterialTheme.typography.bodySmall)
            }

        }
    }
}




